import { useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentType, CSSProperties, ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Money, Text } from '@belong/ui';
import UntypedSlider from 'components/v2/Slider/Slider';
import UntypedTooltip from 'components/Tooltip/Tooltip';
import {
  simulateSplitPayments,
  createFinancePlan,
  createSplitItPaymentFirst,
} from 'store/redux/accounts/actions';
import { selectSimulatePayments } from 'store/redux/accounts/selectors';
import { selectSettings } from 'store/redux/settings';
import { DisclosureModal } from 'pages/Accounts/Pages/YourPortfolio/Earnings/disclosure-modal/disclosure-modal';
import { getSplitItUnavailableMessage } from 'pages/Accounts/Components/split-it-unavailable-message';
import { FAILED_PAYMENTS_STRINGS } from '../failed-payments-strings';
import {
  formatFailedPaymentsAmount,
  getFailedPaymentErrorMessage,
} from '../failed-payments-utils';

const SLIDER_STEP = 1;

export const SPLIT_IT_DEFAULT_INSTALLMENTS = 2;

const Slider = UntypedSlider as unknown as ComponentType<{
  max: number;
  min: number;
  disabled?: boolean;
  onChange: (value: number) => void;
  step: number;
  value: number;
  renderTop?: (value: number) => ReactNode;
  renderBottom?: (value: number) => ReactNode;
}>;
const Tooltip = UntypedTooltip as unknown as ComponentType<{
  contentStyle?: CSSProperties;
  children?: ReactNode;
}>;

const TOOLTIP_CONTENT_STYLE = { zIndex: 1000001 };

type SplitItSuccessModel = {
  installments: number;
  monthlyPayment: number;
  upfrontAmount: number;
};

type SplitItSubsectionProps = {
  balanceUniqueIds: string[];
  totalAmount: number;
  upfrontPaymentAccountUniqueId: string | null;
  onCanSubmitChange: (canSubmit: boolean) => void;
  registerSubmit: (submit: (() => Promise<void>) | null) => void;
  onSuccess: (success: SplitItSuccessModel) => void;
  onFailure: () => void;
  onLoadingChange: (loading: boolean) => void;
  hasPrefetchedSimulation?: boolean;
};

export function SplitItSubsection({
  balanceUniqueIds,
  totalAmount,
  upfrontPaymentAccountUniqueId,
  onCanSubmitChange,
  registerSubmit,
  onSuccess,
  onFailure,
  onLoadingChange,
  hasPrefetchedSimulation = false,
}: SplitItSubsectionProps) {
  const dispatch = useDispatch();
  const simulation = useSelector(selectSimulatePayments);
  const settings = useSelector(selectSettings);
  const interestRateValue =
    settings?.commerceSettings?.splitItSettings?.interestRateValue;

  const [monthlyInstallments, setMonthlyInstallments] = useState(
    SPLIT_IT_DEFAULT_INSTALLMENTS,
  );
  const [simulating, setSimulating] = useState(false);
  const sliderTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showDisclosure, setShowDisclosure] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const splitItPaymentFirst = simulation?.splitItPaymentFirst;
  const isPaymentFirst = Boolean(splitItPaymentFirst?.isInScope);
  const availableInstallmentCounts: number[] = isPaymentFirst
    ? (splitItPaymentFirst.availableInstallmentCounts ?? [])
    : [];
  const isSplitItUnavailable =
    isPaymentFirst &&
    (!splitItPaymentFirst.isSplitItAvailable ||
      availableInstallmentCounts.length === 0);
  const isSingleStop =
    isPaymentFirst && availableInstallmentCounts.length === 1;
  const sliderMax =
    isPaymentFirst && availableInstallmentCounts.length > 0
      ? (availableInstallmentCounts[availableInstallmentCounts.length - 1] ??
        12)
      : 12;
  const sliderMin =
    isPaymentFirst && availableInstallmentCounts.length > 0
      ? isSingleStop
        ? sliderMax - 1
        : (availableInstallmentCounts[0] ?? 2)
      : 2;

  const upfrontAmount = isPaymentFirst
    ? Math.abs(splitItPaymentFirst.requiredPaymentAmount ?? 0)
    : 0;
  const financedPrincipal = isPaymentFirst
    ? Math.abs(splitItPaymentFirst.financedPrincipal ?? totalAmount)
    : totalAmount;

  const balancesKey = balanceUniqueIds.join(',');

  const idempotencyKey = useMemo(
    () => crypto.randomUUID(),

    [balancesKey, monthlyInstallments],
  );

  function getMonthlyTotal(): number {
    if (isPaymentFirst) return splitItPaymentFirst.monthlyPayment ?? 0;
    if (simulation?.finance) {
      return (
        simulation.finance.monthlyAmount +
        simulation.finance.monthlyInterestAmount
      );
    }
    return 0;
  }

  function getAprValue(): number | string {
    if (isPaymentFirst && splitItPaymentFirst.apr != null) {
      return splitItPaymentFirst.apr;
    }
    return interestRateValue ?? '';
  }

  function getMonthlyInterestAmount(): number {
    if (isPaymentFirst) {
      const financeSimulation = splitItPaymentFirst.financeSimulation;
      if (
        financeSimulation?.interestAmount &&
        financeSimulation?.installments
      ) {
        return (
          financeSimulation.interestAmount / financeSimulation.installments
        );
      }
      return 0;
    }
    return simulation?.finance?.monthlyInterestAmount ?? 0;
  }

  async function runSimulation(installments: number) {
    if (!balancesKey) return;
    setSimulating(true);
    setError(null);
    try {
      await dispatch(
        simulateSplitPayments({
          installments,
          balanceUniqueIds: balancesKey.split(','),
        }),
      );
    } catch (e: unknown) {
      setError(
        getFailedPaymentErrorMessage(
          e,
          FAILED_PAYMENTS_STRINGS['error.generic'],
        ),
      );
      onFailure();
    } finally {
      setSimulating(false);
    }
  }

  useEffect(() => {
    const isPrefetched =
      hasPrefetchedSimulation &&
      monthlyInstallments === SPLIT_IT_DEFAULT_INSTALLMENTS &&
      Object.keys(simulation).length > 0;
    if (!isPrefetched) {
      runSimulation(monthlyInstallments);
    }
    return () => {
      if (sliderTimeoutRef.current) clearTimeout(sliderTimeoutRef.current);
    };
  }, [balancesKey]);

  useEffect(() => {
    if (!isPaymentFirst || availableInstallmentCounts.length === 0) return;
    const highestAvailable =
      availableInstallmentCounts[availableInstallmentCounts.length - 1];
    if (
      highestAvailable != null &&
      !availableInstallmentCounts.includes(monthlyInstallments)
    ) {
      setMonthlyInstallments(highestAvailable);
      runSimulation(highestAvailable);
    }
  }, [splitItPaymentFirst]);

  function handleSliderChange(value: number) {
    setMonthlyInstallments(value);
    if (sliderTimeoutRef.current) clearTimeout(sliderTimeoutRef.current);
    sliderTimeoutRef.current = setTimeout(() => {
      runSimulation(value);
    }, 300);
  }

  const canSubmit =
    !simulating && !error && !isSplitItUnavailable && !!balancesKey;

  useEffect(() => {
    onCanSubmitChange(canSubmit);
  }, [canSubmit, onCanSubmitChange]);

  useEffect(() => {
    registerSubmit(async () => {
      onLoadingChange(true);
      setError(null);
      try {
        if (isPaymentFirst && splitItPaymentFirst.canStartRequiredPayment) {
          if (!upfrontPaymentAccountUniqueId) {
            setError(FAILED_PAYMENTS_STRINGS['error.missing_payment_method']);
            return;
          }
          await dispatch(
            createSplitItPaymentFirst({
              installments: monthlyInstallments,
              balanceUniqueIds: balanceUniqueIds,
              paymentAccountUniqueId: upfrontPaymentAccountUniqueId,
              idempotencyKey,
            }),
          );
        } else {
          await dispatch(
            createFinancePlan({
              installments: monthlyInstallments,
              balanceUniqueIds: balanceUniqueIds,
            }),
          );
        }
        onSuccess({
          installments: monthlyInstallments,
          monthlyPayment: getMonthlyTotal(),
          upfrontAmount,
        });
      } catch (e: unknown) {
        setError(
          getFailedPaymentErrorMessage(
            e,
            FAILED_PAYMENTS_STRINGS['error.generic'],
          ),
        );
        onFailure();
      } finally {
        onLoadingChange(false);
      }
    });
    return () => registerSubmit(null);
  });

  const monthsLabel = (count: number) =>
    FAILED_PAYMENTS_STRINGS['split.months'].replace('{count}', String(count));

  return (
    <div className="flex flex-col gap-sm">
      <Text variant="h3" fontWeight="semibold">
        {FAILED_PAYMENTS_STRINGS['split.prompt'].replace(
          '{amount}',
          formatFailedPaymentsAmount(totalAmount),
        )}
      </Text>

      <div className="flex flex-col rounded border border-solid border-green bg-green-translucent-light px-sm py-xl">
        {isSplitItUnavailable || error ? (
          <div className="flex min-h-[116px] items-center justify-center text-center">
            <Text variant="p1">
              {getSplitItUnavailableMessage(
                error ||
                  splitItPaymentFirst.message ||
                  splitItPaymentFirst.ineligibilityReason,
              )}
            </Text>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-xs">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-xs">
                  <Text variant="body" fontWeight="semibold">
                    {FAILED_PAYMENTS_STRINGS['split.due_today']}
                  </Text>
                  <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE}>
                    {FAILED_PAYMENTS_STRINGS['split.due_today_tooltip']}
                  </Tooltip>
                </div>
                <Money
                  format="DOLLARS_WITH_CENTS"
                  variant="body"
                  fontWeight="semibold"
                  value={-upfrontAmount}
                />
              </div>
              <div className="flex items-center justify-between">
                <Text variant="body" fontWeight="semibold">
                  {FAILED_PAYMENTS_STRINGS['split.splitting']}
                </Text>
                <Money
                  format="DOLLARS_WITH_CENTS"
                  variant="body"
                  fontWeight="semibold"
                  value={-financedPrincipal}
                />
              </div>
            </div>

            <div className="my-md border-0 border-t border-dashed border-gray" />

            <div className="mb-xs flex flex-col items-center text-center">
              <div className="mb-xs flex items-baseline gap-2xs">
                <Money
                  format="DOLLARS_WITH_CENTS"
                  variant="h2"
                  fontWeight="semibold"
                  value={getMonthlyTotal()}
                />
                <Text variant="body" fontWeight="semibold">
                  {FAILED_PAYMENTS_STRINGS['split.per_month']}
                </Text>
              </div>
              <Text variant="p1" className="text-center">
                {FAILED_PAYMENTS_STRINGS['split.apr_line']
                  .replace('{apr}', String(getAprValue()))
                  .replace(
                    '{interest}',
                    getMonthlyInterestAmount().toLocaleString('en', {
                      useGrouping: false,
                      minimumFractionDigits: 2,
                    }),
                  )}
                <br />
                {FAILED_PAYMENTS_STRINGS['split.due_first']}
              </Text>
            </div>

            <div className="mx-auto w-full max-w-[503px]">
              <Slider
                max={sliderMax}
                min={sliderMin}
                disabled={isSingleStop}
                onChange={handleSliderChange}
                step={SLIDER_STEP}
                value={monthlyInstallments}
                renderTop={(val: number) => (
                  <div className="my-sm flex justify-center">
                    <Text fontWeight="semibold">
                      {FAILED_PAYMENTS_STRINGS['split.installments'].replace(
                        '{count}',
                        String(val),
                      )}
                    </Text>
                  </div>
                )}
                renderBottom={() => (
                  <div className="mt-xs flex items-center justify-between">
                    <Text variant="p1">{monthsLabel(sliderMin)}</Text>
                    <div className="flex items-center gap-xs">
                      <Text variant="p1">{monthsLabel(sliderMax)}</Text>
                      <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE}>
                        {FAILED_PAYMENTS_STRINGS['split.months_tooltip']}
                      </Tooltip>
                    </div>
                  </div>
                )}
              />
            </div>
          </>
        )}
      </div>

      <div className="flex flex-col items-center gap-2xs text-center">
        <Text variant="p1">
          {FAILED_PAYMENTS_STRINGS['split.disclosure_prefix']}
        </Text>
        <Button onClick={() => setShowDisclosure(true)} variant="text">
          {FAILED_PAYMENTS_STRINGS['split.disclosure_cta']}
        </Button>
      </div>

      {showDisclosure && (
        <DisclosureModal
          show={showDisclosure}
          setShow={setShowDisclosure}
          installments={monthlyInstallments}
          balanceIds={balanceUniqueIds}
        />
      )}
    </div>
  );
}
